import { useState } from "react";

// ─── Theme tokens ─────────────────────────────────────────────────────────────
const GREEN = "#00E5A0";
const GREEN_DIM = "rgba(0,229,160,0.12)";
const GREEN_BORDER = "rgba(0,229,160,0.25)";
const BG = "#0a0a0a";
const SURFACE = "#111111";
const SURFACE2 = "#161616";
const BORDER = "rgba(255,255,255,0.07)";
const TEXT = "#ffffff";
const TEXT_MID = "#a0a0a0";
const TEXT_DIM = "#555555";

// ─── Shared styles ────────────────────────────────────────────────────────────
const thStyle = {
  padding: "10px 20px",
  textAlign: "left",
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: TEXT_DIM,
  background: SURFACE,
  borderBottom: `1px solid ${BORDER}`,
};

const tdStyle = {
  padding: "14px 20px",
  borderBottom: `1px solid rgba(255,255,255,0.04)`,
  verticalAlign: "top",
  color: TEXT_MID,
  lineHeight: "1.6",
  fontSize: "13px",
};

const sessionLabelStyle = {
  fontWeight: 700,
  color: TEXT,
  fontSize: "13px",
  fontFamily: "'JetBrains Mono', monospace",
  whiteSpace: "nowrap",
};

// ─── Primitive components ─────────────────────────────────────────────────────
function ResourceLink({ href, children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        color: hovered ? GREEN : "rgba(0,229,160,0.7)",
        textDecoration: "none",
        fontSize: "11px",
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "3px 10px",
        borderRadius: "4px",
        background: hovered ? GREEN_DIM : "rgba(0,229,160,0.06)",
        border: `1px solid ${hovered ? GREEN_BORDER : "rgba(0,229,160,0.12)"}`,
        transition: "all 0.15s",
        marginBottom: "4px",
        marginRight: "4px",
        letterSpacing: "0.3px",
      }}
    >
      {children}
    </a>
  );
}

function Badge({ label, color }) {
  return (
    <span
      style={{
        fontSize: "10px",
        fontWeight: 700,
        padding: "3px 9px",
        borderRadius: "3px",
        background: `${color}15`,
        color,
        border: `1px solid ${color}30`,
        letterSpacing: "0.8px",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        fontFamily: "'JetBrains Mono', monospace",
      }}
    >
      {label}
    </span>
  );
}

function ImportanceBadge({ text }) {
  return (
    <span
      style={{
        fontSize: "10px",
        fontWeight: 800,
        padding: "4px 10px",
        borderRadius: "3px",
        background: "rgba(255,60,60,0.12)",
        color: "#ff6060",
        border: "1px solid rgba(255,60,60,0.25)",
        letterSpacing: "0.8px",
        textTransform: "uppercase",
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontFamily: "'JetBrains Mono', monospace",
      }}
    >
      🔥 {text}
    </span>
  );
}

function TableRow({ session, content, resources, extra }) {
  const [hovered, setHovered] = useState(false);
  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "rgba(0,229,160,0.03)" : "transparent",
        transition: "background 0.15s",
      }}
    >
      <td style={{ ...tdStyle, ...sessionLabelStyle }}>{session}</td>
      <td style={tdStyle}>{content}</td>
      <td style={tdStyle}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
          {resources}
        </div>
      </td>
      {extra !== undefined && <td style={tdStyle}>{extra}</td>}
    </tr>
  );
}

// ─── Section tables ───────────────────────────────────────────────────────────
function HashingTable() {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {["Session", "Content", "Resources"].map((h) => (
              <th key={h} style={thStyle}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <TableRow
            session="Foundation"
            content="Hashing Part 0, 1, 2"
            resources={
              <>
                <ResourceLink href="https://drive.google.com/file/d/10ZssTVcwkRdsoGlZFPHBXvVZa6OROky8/view">
                  📹 Part 0
                </ResourceLink>
                <ResourceLink href="https://drive.google.com/file/d/1BEv_Ew72EPjLBV6XX6d2QvMF-hjfIkAI/view">
                  📹 Part 1
                </ResourceLink>
                <ResourceLink href="https://drive.google.com/file/d/15hLKYt41unWwucyIBHNgR4IzVUKpVAsS/view">
                  📹 Part 2
                </ResourceLink>
                <ResourceLink href="https://www.desiqna.in/hashing+find+frequency+of+each+number+in+the+array">
                  📖 Document
                </ResourceLink>
              </>
            }
          />
          <TableRow
            session="Sessions 0–9"
            content="Concept Building Sessions"
            resources={
              <>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <ResourceLink key={n} href="#">
                    📋 Session-{n}
                  </ResourceLink>
                ))}
              </>
            }
          />
          {[
            [
              "Session 10",
              "Amazon OA Session",
              [
                ["📹 Part 1", "#"],
                ["📹 Part 2", "#"],
                ["📋 DOC", "#"],
              ],
            ],
            [
              "Session 11",
              "Zscaler OA",
              [
                ["📋 Video", "#"],
                ["📋 DOC", "#"],
              ],
            ],
            [
              "Session 12",
              "Interview Level",
              [
                ["📋 Video", "#"],
                ["📋 Doc 1", "#"],
                ["📋 Doc 2", "#"],
              ],
            ],
            [
              "Session 13",
              "Interview Level",
              [
                ["📋 Video", "#"],
                ["📋 DOC1", "#"],
                ["📋 DOC2", "#"],
              ],
            ],
            ["Recap", "Recap Session", [["📋 Video", "#"]]],
            [
              "Session 14",
              "Interview Level",
              [
                ["📋 Video", "#"],
                ["📋 DOC", "#"],
              ],
            ],
            [
              "Session 15",
              "Interview Level",
              [
                ["📋 Video", "#"],
                ["📋 DOC", "#"],
              ],
            ],
            [
              "Session 16",
              "Interview Level",
              [
                ["📋 Video", "#"],
                ["📋 DOC", "#"],
              ],
            ],
            [
              "Session 17",
              "Hashing · Prefix Sum · Suffix Sum",
              [
                ["📋 Video", "#"],
                ["📋 Doc", "#"],
              ],
            ],
            [
              "Session 18",
              "Amazon OA 2025 P1",
              [
                ["📋 Video", "#"],
                ["📋 Doc", "#"],
              ],
            ],
            [
              "Session 19",
              "Hashing · Prefix Sum · Suffix Sum",
              [
                ["📋 Video 1", "#"],
                ["📋 Video 2", "#"],
                ["📋 Doc", "#"],
              ],
            ],
            [
              "Session 20",
              "LeetCode Contest",
              [
                ["📋 Video", "#"],
                ["📋 Doc", "#"],
              ],
            ],
          ].map(([s, c, r]) => (
            <TableRow
              key={s}
              session={s}
              content={c}
              resources={r.map(([l, h]) => (
                <ResourceLink key={l} href={h}>
                  {l}
                </ResourceLink>
              ))}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TwoPointerTable() {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {["Session", "Content", "Resources", "Tag"].map((h) => (
              <th key={h} style={thStyle}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <TableRow
            session="Master-class"
            content="Two Pointer Template + Master-class"
            resources={
              <>
                <ResourceLink href="#">📹 Master Video</ResourceLink>
                <ResourceLink href="#">📋 Very Imp Doc</ResourceLink>
              </>
            }
            extra={<ImportanceBadge text="Very Important" />}
          />
          <TableRow
            session="Foundation"
            content="Strong Foundational Video + Google Interview"
            resources={
              <>
                <ResourceLink href="#">📋 Video 1</ResourceLink>
                <ResourceLink href="#">📋 Google Interview Video</ResourceLink>
              </>
            }
            extra={<Badge label="Google" color="#00E5A0" />}
          />
          <TableRow
            session="Microsoft OA"
            content="Microsoft Interview Session"
            resources={
              <>
                <ResourceLink href="#">📋 Video</ResourceLink>
                <ResourceLink href="#">📋 Doc</ResourceLink>
              </>
            }
            extra={<Badge label="Microsoft" color="#60a5fa" />}
          />
          <TableRow
            session="Google SDE-3 Part 1"
            content="Google SDE-3 Interview Part 1"
            resources={
              <>
                <ResourceLink href="#">📋 Video</ResourceLink>
                <ResourceLink href="#">📋 DOC</ResourceLink>
              </>
            }
            extra={<Badge label="Google" color="#00E5A0" />}
          />
          <TableRow
            session="Google SDE-3 Part 2"
            content="Google SDE-3 Interview Part 2"
            resources={
              <>
                <ResourceLink href="#">📋 Video</ResourceLink>
                <ResourceLink href="#">📋 DOC</ResourceLink>
              </>
            }
            extra={<Badge label="Google" color="#00E5A0" />}
          />
        </tbody>
      </table>
    </div>
  );
}

function BinarySearchTable() {
  const rows = [
    {
      s: "Session 1",
      l: "Interview Level",
      r: (
        <>
          <ResourceLink href="#">Video</ResourceLink>
          <ResourceLink href="#">Doc</ResourceLink>
        </>
      ),
    },
    {
      s: "Session 2",
      l: "Interview Level",
      r: (
        <>
          <ResourceLink href="#">📹 Part 1</ResourceLink>
          <ResourceLink href="#">📹 Part 2</ResourceLink>
          <ResourceLink href="#">📋 Doc</ResourceLink>
        </>
      ),
    },
    {
      s: "Session 3",
      l: "Interview Level",
      r: (
        <>
          <ResourceLink href="#">Video</ResourceLink>
          <ResourceLink href="#">DOC</ResourceLink>
        </>
      ),
    },
    {
      s: "Session 4",
      l: "Interview Level",
      r: (
        <>
          <ResourceLink href="#">Video</ResourceLink>
          <ResourceLink href="#">DOC</ResourceLink>
        </>
      ),
    },
    {
      s: "Session 5",
      l: "Interview Level",
      r: (
        <>
          <ResourceLink href="#">Video</ResourceLink>
          <ResourceLink href="#">DOC</ResourceLink>
        </>
      ),
    },
    {
      s: "Session 6",
      l: "Microsoft OA",
      r: (
        <>
          <ResourceLink href="#">Video</ResourceLink>
          <ResourceLink href="#">Doc</ResourceLink>
        </>
      ),
    },
  ];
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {["Session", "Level", "Resources"].map((h) => (
              <th key={h} style={thStyle}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <TableRow
              key={row.s}
              session={row.s}
              content={<Badge label={row.l} color="#f97316" />}
              resources={row.r}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GreedyTable() {
  const rows = [
    {
      t: "Introduction",
      c: "Intro + LeetCode Contest",
      p: <Badge label="LeetCode" color="#f59e0b" />,
      r: (
        <>
          <ResourceLink href="#">📋 Video</ResourceLink>
          <ResourceLink href="#">📋 DOC 0</ResourceLink>
          <ResourceLink href="#">📋 DOC 1</ResourceLink>
        </>
      ),
    },
    {
      t: "Advanced",
      c: "Codeforces (D) Problem Solving",
      p: <Badge label="Codeforces" color="#a78bfa" />,
      r: (
        <>
          <ResourceLink href="#">📋 Video</ResourceLink>
          <ResourceLink href="#">📋 DOC</ResourceLink>
        </>
      ),
    },
    {
      t: "Competitive",
      c: "Codechef 1900+ Rated",
      p: <Badge label="CodeChef" color="#fbbf24" />,
      r: (
        <>
          <ResourceLink href="#">📋 Video</ResourceLink>
          <ResourceLink href="#">📋 DOC</ResourceLink>
        </>
      ),
    },
    {
      t: "Industry OA",
      c: "IBM OA",
      p: <Badge label="IBM" color="#818cf8" />,
      r: (
        <>
          <ResourceLink href="#">📋 Video</ResourceLink>
          <ResourceLink href="#">📋 DOC</ResourceLink>
        </>
      ),
    },
    {
      t: "Advanced",
      c: "Codechef Problem Session",
      p: <Badge label="CodeChef" color="#fbbf24" />,
      r: (
        <>
          <ResourceLink href="#">📋 Video</ResourceLink>
          <ResourceLink href="#">📋 DOC</ResourceLink>
        </>
      ),
    },
    {
      t: "Hybrid",
      c: "Greedy + Intuition + Two Pointer",
      p: <Badge label="Goldman Sachs" color="#60a5fa" />,
      r: (
        <>
          <ResourceLink href="#">📋 Video</ResourceLink>
          <ResourceLink href="#">📋 DOC</ResourceLink>
        </>
      ),
    },
    {
      t: "Advanced",
      c: "Greedy + Math Analysis",
      p: <Badge label="Atlassian 70LPA" color={GREEN} />,
      r: (
        <>
          <ResourceLink href="#">📋 Video</ResourceLink>
          <ResourceLink href="#">📋 DOC</ResourceLink>
        </>
      ),
    },
  ];
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {["Topic", "Content", "Platform", "Resources"].map((h) => (
              <th key={h} style={thStyle}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <TableRow
              key={i}
              session={row.t}
              content={
                <>
                  <div
                    style={{
                      color: TEXT,
                      marginBottom: "6px",
                      fontWeight: 500,
                    }}
                  >
                    {row.c}
                  </div>
                  {row.p}
                </>
              }
              resources={row.r}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecursionTable() {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {["Session", "Content", "Resources"].map((h) => (
              <th key={h} style={thStyle}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <TableRow
            session="Session 1"
            content="Introduction (Explanation by Kumar K)"
            resources={
              <ResourceLink href="https://youtu.be/vky0RHci1Oc">
                Video
              </ResourceLink>
            }
          />
        </tbody>
      </table>
    </div>
  );
}

function DPTable() {
  const rows = [
    {
      s: "Session 1",
      l: <Badge label="Interview Level" color="#f97316" />,
      r: (
        <>
          <ResourceLink href="#">Video</ResourceLink>
          <ResourceLink href="#">DOC</ResourceLink>
        </>
      ),
    },
    {
      s: "Session 2",
      l: <Badge label="Interview Level" color="#f97316" />,
      r: (
        <>
          <ResourceLink href="#">Video</ResourceLink>
          <ResourceLink href="#">DOC</ResourceLink>
        </>
      ),
    },
    {
      s: "Session 3",
      l: <Badge label="Interview Level" color="#f97316" />,
      r: (
        <>
          <ResourceLink href="#">Video</ResourceLink>
          <ResourceLink href="#">DOC</ResourceLink>
        </>
      ),
    },
    {
      s: "Session 4",
      l: <Badge label="AtCoder DP" color="#a78bfa" />,
      r: (
        <>
          <ResourceLink href="#">Video</ResourceLink>
          <ResourceLink href="#">DOC</ResourceLink>
          <ResourceLink href="#">AtCoder DP 1</ResourceLink>
          <ResourceLink href="#">AtCoder DP 2</ResourceLink>
        </>
      ),
    },
    {
      s: "Session 5",
      l: <Badge label="PayPal OA" color="#60a5fa" />,
      r: (
        <>
          <ResourceLink href="#">Video</ResourceLink>
          <ResourceLink href="#">DOC</ResourceLink>
        </>
      ),
    },
  ];
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {["Session", "Level", "Resources"].map((h) => (
              <th key={h} style={thStyle}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <TableRow
              key={row.s}
              session={row.s}
              content={row.l}
              resources={row.r}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GraphTable() {
  const rows = [
    {
      s: "Intro",
      c: "Intro to graphs",
      r: (
        <>
          <ResourceLink href="#">Video</ResourceLink>
          <ResourceLink href="#">DOC</ResourceLink>
        </>
      ),
    },
    {
      s: "Session 1",
      c: "BFS and its applications",
      r: <ResourceLink href="#">✅ Video</ResourceLink>,
    },
    {
      s: "Session 2",
      c: "BFS — Graph OA level (Thoughts + Intuition)",
      r: (
        <>
          <ResourceLink href="#">✅ Video</ResourceLink>
          <ResourceLink href="#">DOC</ResourceLink>
        </>
      ),
    },
    {
      s: "Session 3",
      c: "BFS — Intermediate Level Trick + Understanding",
      r: (
        <>
          <ResourceLink href="#">✅ Video</ResourceLink>
          <ResourceLink href="#">DOC</ResourceLink>
        </>
      ),
    },
    {
      s: "Session 4",
      c: "BFS — Uber SDE Interview 2025",
      r: (
        <>
          <ResourceLink href="#">✅ Video</ResourceLink>
          <ResourceLink href="#">DOC</ResourceLink>
        </>
      ),
    },
    {
      s: "Session 5",
      c: "DFS Master Session (Thoughts + Process + Intuition)",
      r: (
        <>
          <ResourceLink href="#">✅ Video</ResourceLink>
          <ResourceLink href="#">DOC</ResourceLink>
        </>
      ),
    },
  ];
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {["Session", "Content", "Resources"].map((h) => (
              <th key={h} style={thStyle}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <TableRow
              key={row.s}
              session={row.s}
              content={row.c}
              resources={row.r}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MathTable() {
  const rows = [
    {
      s: "Modulo Knowledge",
      c: "Fundamental concepts",
      r: (
        <>
          <ResourceLink href="#">📋 Video</ResourceLink>
          <ResourceLink href="#">📋 DOC</ResourceLink>
        </>
      ),
    },
    {
      s: "Math (nCR)",
      c: "Foundational Concept",
      r: (
        <>
          <ResourceLink href="#">📋 Video</ResourceLink>
          <ResourceLink href="#">📋 DOC</ResourceLink>
        </>
      ),
    },
    {
      s: "Factor + Prime Factor",
      c: "Intro 0",
      r: (
        <>
          <ResourceLink href="#">📋 Video</ResourceLink>
          <ResourceLink href="#">📋 DOC</ResourceLink>
        </>
      ),
    },
    {
      s: "Factor + Prime Factor",
      c: "Intro 1",
      r: (
        <>
          <ResourceLink href="https://drive.google.com/file/d/1ThcGpV6DVjrXG_J5sgsFKw6CN4a5FFkV/view?usp=sharing">
            📋 Video
          </ResourceLink>
          <ResourceLink href="https://docs.google.com/document/d/15ZFZuxIGZgwbrT9NeBfUT00c-6KMjpuN-M7JRl8HFVI/edit?usp=sharing">
            📋 DOC
          </ResourceLink>
        </>
      ),
    },
    {
      s: "Factor + Prime Factor",
      c: "Codechef Contest P0, P1",
      r: (
        <>
          <ResourceLink href="https://drive.google.com/file/d/1ginV-ZWVf-iE_KBSRWeWaoMhnER0hTq0/view?usp=sharing">
            📋 Video 1
          </ResourceLink>
          <ResourceLink href="https://drive.google.com/file/d/1w6MQnIwMLYmFgq7SM04brNFxm7sAGKBd/view?usp=sharing">
            📋 Video 2
          </ResourceLink>
          <ResourceLink href="https://docs.google.com/document/d/15ZFZuxIGZgwbrT9NeBfUT00c-6KMjpuN-M7JRl8HFVI/edit?usp=sharing">
            📋 DOC
          </ResourceLink>
        </>
      ),
    },
    {
      s: "Factor Optimization",
      c: "Google SDE-2 Interview",
      r: (
        <>
          <ResourceLink href="https://drive.google.com/file/d/1_85y4J9Qz4Nbm9M5Y3_j0TvcvNfBImoX/view?usp=sharing">
            📋 Video 1
          </ResourceLink>
          <ResourceLink href="https://drive.google.com/file/d/1eKbOeqZJTuA373LPW8RkVEJAy5TDEd0S/view?usp=sharing">
            📋 Video 2
          </ResourceLink>
          <ResourceLink href="https://drive.google.com/file/d/15je83OPb5H1Frc91tQ6CxNcun38rQlbX/view?usp=sharing">
            📋 Video 3
          </ResourceLink>
          <ResourceLink href="https://docs.google.com/document/d/1lL9K7c5REKXIl6PKm7AV__hH5n6jU5B8XJTNus9Km5s/edit?tab=t.0">
            📋 DOC
          </ResourceLink>
        </>
      ),
    },
  ];
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {["Topic", "Content", "Resources"].map((h) => (
              <th key={h} style={thStyle}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <TableRow
              key={i}
              session={row.s}
              content={row.c}
              resources={row.r}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TreesTable() {
  const rows = [
    {
      s: "Session 1",
      c: "Fundamental tree concepts",
      r: (
        <>
          <ResourceLink href="https://drive.google.com/file/d/1QsrOkTm1xPbRtMC0T78hPF6iXWkfOn1q/view?usp=sharing">
            📋 Video
          </ResourceLink>
          <ResourceLink href="https://docs.google.com/document/d/1S8zLM0nvHY3OrIUwTjJAkg6misgu7CjIbIqYPMtdbnY/edit">
            📋 DOC
          </ResourceLink>
        </>
      ),
    },
    {
      s: "Session 2",
      c: "Advanced tree traversal",
      r: (
        <>
          <ResourceLink href="https://drive.google.com/file/d/1I8nsaqmppLqep38pzoShu44Kh-TWdulx/view?usp=sharing">
            📋 Video
          </ResourceLink>
          <ResourceLink href="https://docs.google.com/document/d/11_n4swLlRGHHgVzZV9BCgpjZ5mpY5BHLuS1gp7PTazk/edit">
            📋 DOC
          </ResourceLink>
        </>
      ),
    },
    {
      s: "Session 3",
      c: "Google SWE Intern OA",
      r: (
        <>
          <ResourceLink href="https://drive.google.com/file/d/1Sa1XGzsWO-l2avRs4L02al04abY7KCnQ/view?usp=sharing">
            📋 Video
          </ResourceLink>
          <ResourceLink href="https://docs.google.com/document/d/1yV1H5gJBn7SVYXiWOLL_kSWHZA-2bDrjjyF8MLRMW_I/edit?tab=t.0">
            📋 DOC
          </ResourceLink>
        </>
      ),
    },
    {
      s: "Session 4",
      c: "Tree optimization techniques",
      r: (
        <>
          <ResourceLink href="https://drive.google.com/file/d/1XxylhrHq02FVYXna66dj5FnUN75SNAjG/view?usp=sharing">
            📋 Video
          </ResourceLink>
          <ResourceLink href="https://docs.google.com/document/d/11_n4swLlRGHHgVzZV9BCgpjZ5mpY5BHLuS1gp7PTazk/edit">
            📋 DOC
          </ResourceLink>
        </>
      ),
    },
    {
      s: "Session 5",
      c: "VmWare OA",
      r: (
        <>
          <ResourceLink href="https://drive.google.com/file/d/1JodwZaaFIYxXsg6qVbMVg_vz7HcfnNzd/view?usp=sharing">
            📋 Video
          </ResourceLink>
          <ResourceLink href="https://docs.google.com/document/d/1e3HmIlhVlT4-WEzNy5TMb0k3DLW6jZdOzy584guWgyw/edit">
            📋 DOC
          </ResourceLink>
        </>
      ),
    },
  ];
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {["Session", "Content", "Resources"].map((h) => (
              <th key={h} style={thStyle}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <TableRow
              key={row.s}
              session={row.s}
              content={row.c}
              resources={row.r}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Section metadata ─────────────────────────────────────────────────────────
const SECTION_META = [
  {
    key: "hashing",
    icon: "#",
    label: "Hashing · Prefix · Suffix",
    accent: GREEN,
    sessions: 20,
    tag: "Foundation",
    Table: HashingTable,
  },
  {
    key: "twoPointer",
    icon: "»",
    label: "Two Pointer",
    accent: "#60a5fa",
    sessions: 5,
    tag: "Core",
    Table: TwoPointerTable,
  },
  {
    key: "binarySearch",
    icon: "⌖",
    label: "Binary Search",
    accent: "#f97316",
    sessions: 6,
    tag: "Core",
    Table: BinarySearchTable,
  },
  {
    key: "greedy",
    icon: "◈",
    label: "Greedy Algorithms",
    accent: "#fbbf24",
    sessions: 7,
    tag: "Advanced",
    Table: GreedyTable,
  },
  {
    key: "recursion",
    icon: "∞",
    label: "Recursion",
    accent: "#a78bfa",
    sessions: 1,
    tag: "Foundation",
    Table: RecursionTable,
  },
  {
    key: "dp",
    icon: "▦",
    label: "Dynamic Programming",
    accent: "#06B6D4",
    sessions: 5,
    tag: "Advanced",
    Table: DPTable,
  },
  {
    key: "graph",
    icon: "⬡",
    label: "Graph",
    accent: GREEN,
    sessions: 6,
    tag: "Advanced",
    Table: GraphTable,
  },
  {
    key: "math",
    icon: "∑",
    label: "Math",
    accent: "#f87171",
    sessions: 6,
    tag: "Core",
    Table: MathTable,
  },
  {
    key: "trees",
    icon: "⌥",
    label: "Trees",
    accent: "#34d399",
    sessions: 5,
    tag: "Advanced",
    Table: TreesTable,
  },
];

const TOTAL_SESSIONS = SECTION_META.reduce((a, b) => a + b.sessions, 0);

// ─── Accordion header ─────────────────────────────────────────────────────────
function SectionHeader({ meta, isOpen, onToggle }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={() => onToggle(meta.key)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: "100%",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
        padding: "18px 24px",
        display: "flex",
        alignItems: "center",
        gap: "16px",
        transition: "all 0.2s",
        borderBottom: isOpen ? `1px solid ${meta.accent}20` : "none",
        background: isOpen
          ? `${meta.accent}08`
          : hovered
            ? "rgba(255,255,255,0.02)"
            : "transparent",
      }}
    >
      {/* Symbol */}
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "6px",
          flexShrink: 0,
          background: isOpen ? `${meta.accent}15` : "rgba(255,255,255,0.04)",
          border: `1px solid ${isOpen ? meta.accent + "40" : BORDER}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "18px",
          fontFamily: "'JetBrains Mono', monospace",
          color: isOpen ? meta.accent : TEXT_DIM,
          fontWeight: 700,
          transition: "all 0.2s",
        }}
      >
        {meta.icon}
      </div>

      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: "15px",
            fontWeight: 700,
            color: isOpen ? TEXT : "#c0c0c0",
            letterSpacing: "-0.2px",
          }}
        >
          {meta.label}
        </div>
        <div
          style={{
            fontSize: "11px",
            color: TEXT_DIM,
            marginTop: "2px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          <span>{meta.sessions} sessions</span>
          <span style={{ color: "#333" }}>·</span>
          <span style={{ color: isOpen ? meta.accent : TEXT_DIM }}>
            {meta.tag}
          </span>
        </div>
      </div>

      {/* Progress bar (decorative) */}
      <div
        style={{
          width: "80px",
          height: "3px",
          background: "rgba(255,255,255,0.06)",
          borderRadius: "2px",
          overflow: "hidden",
          display: "flex",
        }}
      >
        <div
          style={{
            width: isOpen ? "60%" : "0%",
            background: meta.accent,
            transition: "width 0.4s ease",
            borderRadius: "2px",
          }}
        />
      </div>

      <div
        style={{
          width: "24px",
          height: "24px",
          borderRadius: "4px",
          background: isOpen ? `${meta.accent}15` : "transparent",
          border: `1px solid ${isOpen ? meta.accent + "30" : BORDER}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: isOpen ? meta.accent : TEXT_DIM,
          fontSize: "12px",
          transition: "all 0.2s",
          transform: isOpen ? "rotate(180deg)" : "none",
          fontFamily: "'JetBrains Mono', monospace",
        }}
      >
        ▾
      </div>
    </button>
  );
}

// ─── Topic pill ───────────────────────────────────────────────────────────────
function TopicPill({ meta, active, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: active
          ? `${meta.accent}15`
          : hovered
            ? "rgba(255,255,255,0.04)"
            : "transparent",
        border: `1px solid ${active ? meta.accent + "50" : BORDER}`,
        borderRadius: "6px",
        padding: "8px 14px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        transition: "all 0.15s",
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          fontSize: "14px",
          fontFamily: "'JetBrains Mono', monospace",
          color: active ? meta.accent : TEXT_DIM,
        }}
      >
        {meta.icon}
      </span>
      <span
        style={{
          fontSize: "12px",
          fontWeight: 600,
          color: active ? TEXT : TEXT_MID,
          letterSpacing: "0.2px",
        }}
      >
        {meta.label}
      </span>
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function DSAStudyPlanner() {
  const initOpen = Object.fromEntries(
    SECTION_META.map((m, i) => [m.key, i === 0]),
  );
  const [openSections, setOpenSections] = useState(initOpen);

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const jumpTo = (key) => {
    setOpenSections((prev) => ({
      ...Object.fromEntries(Object.keys(prev).map((k) => [k, false])),
      [key]: true,
    }));
    setTimeout(() => {
      document
        .getElementById(`section-${key}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: BG,
        fontFamily: "'DM Sans', -apple-system, sans-serif",
        color: TEXT,
      }}
    >
      {/* Subtle grid */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.015) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.015) 1px,transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      {/* Green glow top-right */}
      <div
        style={{
          position: "fixed",
          top: "-300px",
          right: "-100px",
          width: "600px",
          height: "600px",
          background: `radial-gradient(circle,${GREEN}12 0%,transparent 65%)`,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          maxWidth: "1080px",
          margin: "0 auto",
          padding: "0 24px 80px",
        }}
      >
        {/* ── HERO ── */}
        <div style={{ padding: "56px 0 48px" }}>
          {/* Nav brand */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "56px",
            }}
          >
            <div
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "5px",
                background: GREEN,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                fontWeight: 900,
                color: "#000",
              }}
            >
              K
            </div>
            <span
              style={{
                fontWeight: 800,
                fontSize: "16px",
                letterSpacing: "-0.3px",
              }}
            >
              Kumar K
            </span>
            <div
              style={{
                marginLeft: "8px",
                fontSize: "10px",
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: "3px",
                background: GREEN_DIM,
                color: GREEN,
                border: `1px solid ${GREEN_BORDER}`,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              DSA Sheet
            </div>
          </div>

          {/* Badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "5px 12px",
              borderRadius: "4px",
              background: GREEN_DIM,
              border: `1px solid ${GREEN_BORDER}`,
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: GREEN,
                animation: "pulse 2s infinite",
              }}
            />
            <span
              style={{
                fontSize: "11px",
                fontWeight: 700,
                color: GREEN,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              450+ Curated Problems
            </span>
          </div>

          {/* Headline */}
          <h1
            style={{
              fontSize: "clamp(36px,6vw,64px)",
              fontWeight: 900,
              letterSpacing: "-2px",
              lineHeight: 1.05,
              marginBottom: "0",
            }}
          >
            Crack DSA with
            <br />
            <span style={{ color: GREEN }}>Kumar K's</span>
            <br />
            Sheet
          </h1>

          {/* Subtext */}
          <p
            style={{
              fontSize: "16px",
              color: TEXT_MID,
              marginTop: "20px",
              maxWidth: "520px",
              lineHeight: 1.7,
            }}
          >
            The{" "}
            <span style={{ color: TEXT, fontWeight: 600 }}>
              most structured DSA roadmap
            </span>{" "}
            for placements &amp; competitive programming — topic-wise,
            handpicked, with zero fluff.
          </p>
          <p
            style={{
              fontSize: "14px",
              color: TEXT_DIM,
              marginTop: "12px",
              maxWidth: "520px",
              lineHeight: 1.7,
            }}
          >
            Meticulously curated after in-depth analysis of OA &amp; interview
            questions at{" "}
            <span style={{ color: TEXT_MID }}>
              Amazon, Google, Microsoft, Apple
            </span>{" "}
            and many more for the 2025–2026 hiring cycles.
          </p>

          {/* CTA row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginTop: "32px",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => jumpTo("hashing")}
              style={{
                padding: "12px 24px",
                borderRadius: "6px",
                background: GREEN,
                color: "#000",
                fontWeight: 800,
                fontSize: "14px",
                border: "none",
                cursor: "pointer",
                letterSpacing: "-0.2px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              Start the Sheet →
            </button>
            <button
              style={{
                padding: "12px 24px",
                borderRadius: "6px",
                background: "transparent",
                color: TEXT_MID,
                fontWeight: 600,
                fontSize: "14px",
                border: `1px solid ${BORDER}`,
                cursor: "pointer",
              }}
            >
              ↗ Track Progress
            </button>
          </div>

          {/* Stat bar */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "32px",
              marginTop: "48px",
              paddingTop: "32px",
              borderTop: `1px solid ${BORDER}`,
            }}
          >
            {[
              { value: "450+", label: "PROBLEMS" },
              { value: `${TOTAL_SESSIONS}+`, label: "SESSIONS" },
              { value: "50K+", label: "SOLVERS" },
              { value: "100%", label: "FREE" },
            ].map((stat) => (
              <div key={stat.label}>
                <div
                  style={{
                    fontSize: "28px",
                    fontWeight: 900,
                    color: TEXT,
                    letterSpacing: "-1px",
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  {stat.value}
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    color: TEXT_DIM,
                    fontWeight: 700,
                    letterSpacing: "2px",
                    marginTop: "2px",
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── What you'll achieve ── */}
        <div
          style={{
            marginBottom: "40px",
            padding: "28px",
            borderRadius: "8px",
            background: SURFACE,
            border: `1px solid ${BORDER}`,
          }}
        >
          <div
            style={{
              fontSize: "11px",
              fontWeight: 700,
              color: GREEN,
              letterSpacing: "2px",
              textTransform: "uppercase",
              fontFamily: "'JetBrains Mono', monospace",
              marginBottom: "16px",
            }}
          >
            // This sheet will help you achieve
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            {[
              { n: "01", t: "DSA prep for Interview", s: "till MAANG level" },
              { n: "02", t: "DSA prep for OA", s: "till MAANG level" },
              { n: "03", t: "DSA prep for CP", s: "Competitive Programming" },
            ].map((item) => (
              <div
                key={item.n}
                style={{
                  flex: "1 1 200px",
                  padding: "16px",
                  borderRadius: "6px",
                  background: SURFACE2,
                  border: `1px solid ${BORDER}`,
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    fontFamily: "'JetBrains Mono', monospace",
                    color: GREEN,
                    fontWeight: 700,
                    marginBottom: "8px",
                  }}
                >
                  {item.n}
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: TEXT,
                    marginBottom: "4px",
                  }}
                >
                  {item.t}
                </div>
                <div style={{ fontSize: "12px", color: TEXT_DIM }}>
                  {item.s}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Topics covered scrollbar ── */}
        <div
          style={{
            marginBottom: "12px",
            fontSize: "10px",
            fontWeight: 700,
            color: TEXT_DIM,
            letterSpacing: "2px",
            textTransform: "uppercase",
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          Topics covered
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            marginBottom: "40px",
          }}
        >
          {SECTION_META.map((meta) => (
            <TopicPill
              key={meta.key}
              meta={meta}
              active={openSections[meta.key]}
              onClick={() => jumpTo(meta.key)}
            />
          ))}
        </div>

        {/* ── Accordion sections ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {SECTION_META.map((meta) => {
            const TableComponent = meta.Table;
            return (
              <div
                key={meta.key}
                id={`section-${meta.key}`}
                style={{
                  borderRadius: "8px",
                  background: SURFACE,
                  border: `1px solid ${openSections[meta.key] ? meta.accent + "25" : BORDER}`,
                  overflow: "hidden",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  boxShadow: openSections[meta.key]
                    ? `0 0 30px ${meta.accent}08`
                    : "none",
                }}
              >
                <SectionHeader
                  meta={meta}
                  isOpen={openSections[meta.key]}
                  onToggle={toggleSection}
                />
                {openSections[meta.key] && (
                  <div
                    style={{
                      animation: "fadeSlide 0.2s ease",
                      borderTop: `1px solid ${BORDER}`,
                    }}
                  >
                    <TableComponent />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            marginTop: "64px",
            paddingTop: "24px",
            borderTop: `1px solid ${BORDER}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <span
            style={{
              fontSize: "12px",
              color: TEXT_DIM,
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            Kumar K DSA Sheet · 2025–2026
          </span>
          <span style={{ fontSize: "12px", color: TEXT_DIM }}>
            <span style={{ color: GREEN }}>●</span> Live · Actively Monitored
          </span>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;700&display=swap');
        @keyframes fadeSlide { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:5px;height:5px}
        ::-webkit-scrollbar-track{background:${SURFACE}}
        ::-webkit-scrollbar-thumb{background:${GREEN}40;border-radius:2px}
        ::-webkit-scrollbar-thumb:hover{background:${GREEN}80}
        ::selection{background:${GREEN}30;color:${TEXT}}
      `}</style>
    </div>
  );
}
